import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { isEmpty } from "lodash-es";
import { Fragment, useEffect, useMemo, useState } from "react";

import { useChatContext } from "@/common/contexts/chat";
import { ArrayHelper } from "@/common/helpers/array";
import { DatetimeHelper } from "@/common/helpers/datetime";
import { useAppSelector, useAppThunkDispatch } from "@/common/hooks/redux";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper, ValidationInfo } from "@/common/validation";
import {
  ChatDto,
  ChatParticipantDto,
  ChatParticipantJoinType,
  ChatParticipantStatus,
} from "@/core/api/generated/v1";
import * as chatParticipantsSlice from "@/store/communication/chatParticipantsSlice";

import AppIcon from "../../Icons/AppIcon";
import AppModalTitle from "../../Modals/AppModalTitle";
import PartyLink from "../Party/PartyLink";
import AddChatParticipantsModal from "./AddChatParticipantsModal";
import ChatParticipantAvatar from "./ChatParticipantAvatar";
import { ChatParticipantStatusDisplay } from "./ChatParticipantStatusDisplay";

interface Props {
  open: boolean;
  onClose?: () => void;
  onUpdated?: (newValues?: ChatDto) => void;

  chat: ChatDto;
  allowManage?: boolean;
}

function ChatParticipantsModal({ open, onClose, onUpdated, chat, allowManage }: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const thunkDispatch = useAppThunkDispatch();

  const chatContext = useChatContext();

  const paginatedParticipants = useAppSelector(
    (x) =>
      x.communication.chatParticipants.paginatedChatParticipantMap[chat?.id || ""] || undefined,
  );
  const removeChatParticipantLoadingMap = useAppSelector(
    (x) => x.communication.chatParticipants.loading.removeChatParticipant || {},
  );

  const [__, setValidation] = useState(new ValidationInfo());
  const [prevOpen, setPrevOpen] = useState(false);
  const [isAddParticipantsModalOpen, setIsAddParticipantsModalOpen] = useState(false);

  const participants = paginatedParticipants?.items;
  const activeParticipants = useMemo(
    () => participants?.filter((x) => x.status === ChatParticipantStatus.Active),
    [participants],
  );
  const inactiveParticipants = useMemo(
    () => participants?.filter((x) => x.status !== ChatParticipantStatus.Active),
    [participants],
  );

  useEffect(() => {
    const isOpening = open && !prevOpen;
    // const isClosing = prevOpen && !open;

    if (isOpening) {
      thunkDispatch(
        chatParticipantsSlice.getAllChatParticipants({
          chatId: chat.id!,
        }),
      );
    }
    setPrevOpen(open);
  }, [open]);

  const handleAddParticipantsModalClose = () => {
    setIsAddParticipantsModalOpen(false);
  };

  const handleAddParticipant = () => {
    setIsAddParticipantsModalOpen(true);
  };

  const handleRemoveParticipant = async (participant: ChatParticipantDto) => {
    try {
      await thunkDispatch(
        chatParticipantsSlice.removeChatParticipant({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chatId: chat.id!,
          participantId: participant.id!,
        }),
      );
      enqueueSnackbar(
        `'${participant.personName?.name || participant.email}' removed from the chat.`,
        {
          variant: "success",
        },
      );
    } catch (err) {
      const newValidation = ValidationHelper.handleApiErrorResponse(err);
      setValidation(newValidation);
      if (newValidation.generalError) {
        enqueueSnackbar(newValidation.generalError, {
          variant: "error",
        });
      }
    }
  };

  const renderParticipantListItem = (participant: ChatParticipantDto) => {
    const isMe = participant.id === chatContext.currentParticipant?.id;
    const isActive = participant.status === ChatParticipantStatus.Active;

    return (
      <ListItem
        key={participant.id}
        secondaryAction={
          allowManage &&
          !isMe &&
          isActive && (
            <Button
              color='text'
              loading={removeChatParticipantLoadingMap[participant.id!]}
              onClick={() => handleRemoveParticipant(participant)}
            >
              <AppIcon of='removePerson' />
            </Button>
          )
        }
      >
        <ListItemAvatar>
          <ChatParticipantAvatar participant={participant} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              <Stack direction='row' sx={{ justifyContent: "space-between" }}>
                <span>
                  {participant?.personName?.name || participant?.email}{" "}
                  <ChatParticipantStatusDisplay participant={participant} />{" "}
                  {isMe ? <Chip size='small' color='default' variant='outlined' label='You' /> : ""}
                </span>

                <Typography component='span' variant='caption' color='text.secondary'>
                  {participant.status === ChatParticipantStatus.Active && (
                    <>
                      {participant.joinType === ChatParticipantJoinType.Joined && (
                        <>
                          Joined{" "}
                          {DatetimeHelper.humanizeDateRangeDurationFromNow(
                            participant.joinedAt || participant.createdAt,
                            {
                              isSuffix: true,
                            },
                          )}
                        </>
                      )}

                      {participant.joinType === ChatParticipantJoinType.Added && (
                        <>
                          Added{" "}
                          {DatetimeHelper.humanizeDateRangeDurationFromNow(
                            participant.addedAt || participant.createdAt,
                            {
                              isSuffix: true,
                            },
                          )}
                        </>
                      )}
                    </>
                  )}

                  {participant.status === ChatParticipantStatus.Left && participant.leftAt && (
                    <>
                      Left{" "}
                      {DatetimeHelper.humanizeDateRangeDurationFromNow(participant.leftAt, {
                        isSuffix: true,
                      })}
                    </>
                  )}
                </Typography>
              </Stack>
            </>
          }
          secondary={
            <Stack component='span'>
              <Typography component='span' variant='body2' color='text.secondary'>
                {participant.email}
              </Typography>

              {participant.parties && ArrayHelper.isNotEmpty(participant.parties) && (
                <Typography component='span' variant='body2' color='text.secondary'>
                  Belongs to {participant.parties.length}{" "}
                  {participant.parties.length === 1 ? "party" : "parties"}
                  {!isEmpty(participant.parties) && (
                    <>
                      :{" "}
                      {participant.parties.map((x, i) => (
                        <Fragment key={i}>
                          {i !== 0 && ", "}
                          <PartyLink entity={x.party} />
                        </Fragment>
                      ))}
                    </>
                  )}
                </Typography>
              )}
            </Stack>
          }
        />
      </ListItem>
    );
  };

  return (
    <Box>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
        <AppModalTitle onClose={() => onClose && onClose()}>Chat participants</AppModalTitle>

        <DialogContent>
          <List dense>
            {allowManage && (
              <ListItem sx={{ p: 0 }}>
                <ListItemButton onClick={handleAddParticipant}>
                  <ListItemAvatar>
                    <Avatar>
                      <AppIcon of='invites' />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary='Add participant' />
                </ListItemButton>
              </ListItem>
            )}

            {ArrayHelper.isNotEmpty(activeParticipants) && (
              <>
                <ListSubheader>Active</ListSubheader>{" "}
                {activeParticipants?.map((participant) => renderParticipantListItem(participant))}
              </>
            )}

            {ArrayHelper.isNotEmpty(inactiveParticipants) && (
              <>
                <ListSubheader>Inactive</ListSubheader>
                {inactiveParticipants?.map((participant) => renderParticipantListItem(participant))}
              </>
            )}
          </List>

          {isAddParticipantsModalOpen && (
            <AddChatParticipantsModal
              open={isAddParticipantsModalOpen}
              onClose={handleAddParticipantsModalClose}
              onUpdated={handleAddParticipantsModalClose}
              chat={chat}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ChatParticipantsModal;
